/**
 * CK | Compact Quick-shop wrapper.
 *
 * Wraps the contents of the compact quick-shop modal so we can intercept
 * color-swatch clicks (which would normally navigate to a sibling product's
 * PDP) and instead swap the modal contents in place to the sibling product.
 *
 * Used by snippets/ck-quick-shop-compact.liquid.
 */
if (!customElements.get('ck-quick-shop-compact')) {
  customElements.define(
    'ck-quick-shop-compact',
    class CkQuickShopCompact extends HTMLElement {
      connectedCallback() {
        this._onClick = this.handleClick.bind(this);
        this._onChange = this.handleChange.bind(this);
        this._onSubmit = this.handleSubmit.bind(this);
        this._onModalOpen = this.resetForm.bind(this);
        // Bound directly on this card's own .modal ancestor (not a document-level custom
        // event keyed by product id) so closing the modal always reaches this instance
        // regardless of how theme.js names/dispatches its own modal events.
        this._onModalCloseClick = (e) => {
          if (e.target.closest('.js-modal-close')) this.closeNotify();
        };

        this.addEventListener('click', this._onClick);
        this.addEventListener('change', this._onChange);

        const form = this.querySelector('.ck-qs-compact__form');
        if (form) form.addEventListener('submit', this._onSubmit);

        // Primes the notify panel's variant id / size label / similar-products bucket for
        // products whose default variant is already sold out on first render — handleChange
        // only fires on a later user interaction.
        this.syncNotifyPanel();

        var initSection = this.closest('.product-section');
        var initSelect = initSection && initSection.querySelector('[data-product-select]');
        this._updateSizeLegend(initSelect && initSelect.value);

        // Reset success state if the modal is reopened for the same product.
        const section = this.closest('[data-product-id]');
        this._productId = section && section.dataset.productId;
        if (this._productId) {
          document.addEventListener(
            'modalOpen.QuickShopModal-' + this._productId,
            this._onModalOpen
          );
        }

        // Close the notify overlay the moment the main quick-buy modal's own close (X)
        // button is clicked, so it's never still open the next time the modal is reopened.
        this._modal = this.closest('.modal');
        if (this._modal) this._modal.addEventListener('click', this._onModalCloseClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('change', this._onChange);

        const form = this.querySelector('.ck-qs-compact__form');
        if (form) form.removeEventListener('submit', this._onSubmit);

        if (this._productId) {
          document.removeEventListener(
            'modalOpen.QuickShopModal-' + this._productId,
            this._onModalOpen
          );
        }

        if (this._modal) this._modal.removeEventListener('click', this._onModalCloseClick);
      }

      closeNotify() {
        const notify = this.querySelector('[data-ck-qs-notify]');
        if (notify) {
          notify.hidden = true;
          this.resetNotify(notify);
        }
      }

      async handleSubmit(e) {
        e.preventDefault();

        const form = e.currentTarget;
        const btn = form.querySelector('[data-add-to-cart]');
        if (!btn || btn.disabled) return;

        btn.classList.add('btn--loading');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: new FormData(form)
          });

          const data = await res.json();

          if (!res.ok) {
            btn.classList.remove('btn--loading');
            return;
          }

          this.classList.add('is-added');

          // Notify the theme — CartDrawer rebuilds and opens automatically if
          // cart type is set to drawer.
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { product: data, addToCartBtn: btn }
          }));

        } catch (err) {
          console.error('CK | Quick shop ATC failed:', err);
          btn.classList.remove('btn--loading');
        }
      }

      resetForm() {
        this.classList.remove('is-added');
        const btn = this.querySelector('[data-add-to-cart]');
        if (btn) btn.classList.remove('btn--loading');

        // Belt-and-braces: the notify overlay is already closed by closeNotify() when the
        // modal's own close (X) button is clicked, but also force it closed here on reopen
        // in case the modal was dismissed some other way (e.g. Escape key).
        this.closeNotify();
      }

      handleChange(e) {
        const input = e.target.closest('[data-variant-input]');
        if (!input) return;

        const wrapper = input.closest('.ck-qs-compact__option--size');
        if (wrapper) {
          const valueDisplay = wrapper.querySelector('.ck-qs-compact__option-value');
          if (valueDisplay) valueDisplay.textContent = input.value;
        }

        // Defer so theme.Product has updated [data-product-select] and the ATC button first
        const section = this.closest('.product-section');
        const viewLink = this.querySelector('[data-ck-view-product]');
        if (!section) return;

        setTimeout(() => {
          const productSelect = section.querySelector('[data-product-select]');
          const atcBtn = this.querySelector('[data-add-to-cart]');
          const notify = this.querySelector('[data-ck-qs-notify]');
          // Locked products (coming soon / forced sold out) keep the disabled button and never
          // show the notify panel, even though sizes stay selectable for the wishlist.
          const locked = this.classList.contains('ck-qs-compact--locked');
          const available = this._isVariantAvailable(productSelect && productSelect.value);
          const isSoldOut = locked ? false : (available === null ? !!(atcBtn && atcBtn.disabled) : !available);

          if (viewLink && productSelect && productSelect.value) {
            viewLink.href = viewLink.href.split('?')[0] + '?variant=' + productSelect.value;
          }

          if (atcBtn) atcBtn.hidden = isSoldOut;
          if (viewLink) viewLink.hidden = !isSoldOut;

          if (notify) {
            notify.hidden = !isSoldOut;
            this.resetNotify(notify);
            this.syncNotifyPanel(notify);
          }

          this._updateSizeLegend(productSelect && productSelect.value);
        }, 0);
      }

      _updateSizeLegend(variantId) {
        var legend = this.querySelector('[data-ck-qs-legend]');
        if (!legend) return;

        var data = this._getSizeInventory();
        if (!data || !data.variants) return;

        var lowLine = legend.querySelector('[data-ck-qs-legend-low]');
        var inStockLine = legend.querySelector('[data-ck-qs-legend-in-stock]');

        var isLow = false;
        var isInStock = false;
        if (variantId) {
          var v = data.variants[String(variantId)];
          if (v && v.available) {
            if (v.managed === 'shopify' && v.quantity > 0 && v.quantity <= data.threshold) {
              isLow = true;
            } else {
              isInStock = true;
            }
          }
        }

        if (isLow && lowLine) {
          var textEl = lowLine.querySelector('[data-ck-qs-legend-low-text]');
          var template = lowLine.getAttribute('data-ck-count-template');
          if (textEl && template) {
            textEl.textContent = template.replace('[count]', v.quantity);
          }
        }

        if (lowLine) lowLine.hidden = !isLow;
        if (inStockLine) inStockLine.hidden = !isInStock;
        legend.hidden = !(isLow || isInStock);
      }

      _getSizeInventory() {
        if (this._sizeInventory !== undefined) return this._sizeInventory;
        this._sizeInventory = null;
        var el = this.querySelector('[data-ck-size-inventory]');
        if (el) {
          try {
            this._sizeInventory = JSON.parse(el.textContent);
          } catch (e) {
            this._sizeInventory = null;
          }
        }
        return this._sizeInventory;
      }

      _isVariantAvailable(variantId) {
        if (!variantId) return null;
        if (!this._variantsById) {
          const section = this.closest('.product-section');
          const el = section && section.querySelector('[data-variant-json]');
          if (!el) return null;
          try {
            this._variantsById = {};
            JSON.parse(el.textContent).forEach((v) => {
              this._variantsById[String(v.id)] = v;
            });
          } catch (e) {
            this._variantsById = null;
            return null;
          }
        }
        const variant = this._variantsById[String(variantId)];
        return variant ? variant.available !== false : null;
      }

      // Keeps the reused ck-notify-panel.liquid content (size label, submit button's
      // variant id, which similar-products bucket is shown) in sync with the currently
      // selected size — same info ck-size-selector.js populates on the PDP when a
      // sold-out size is clicked, just driven by variant change here instead of a click
      // since quick buy has no separate "open" step.
      syncNotifyPanel(notify) {
        if (!notify) notify = this.querySelector('[data-ck-qs-notify]');
        if (!notify) return;

        const sizeWrapper = this.querySelector('.ck-qs-compact__option--size');
        const checkedInput = sizeWrapper && sizeWrapper.querySelector('[data-variant-input]:checked');
        const sizeValue = checkedInput ? checkedInput.value : null;

        const section = this.closest('.product-section');
        const productSelect = section && section.querySelector('[data-product-select]');
        const variantId = productSelect ? productSelect.value : '';

        notify.querySelectorAll('[data-ck-notify-submit]').forEach((btn) => {
          btn.dataset.variantId = variantId;
        });

        if (sizeValue) {
          notify.querySelectorAll('[data-ck-notify-size]').forEach((el) => {
            el.textContent = sizeValue;
          });
          notify.querySelectorAll('[data-ck-similar-products]').forEach((block) => {
            block.hidden = block.dataset.size !== sizeValue;
          });
        }
      }

      resetNotify(notify) {
        const form = notify.querySelector('[data-ck-notify-form]');
        const success = notify.querySelector('[data-ck-notify-success]');
        const error = notify.querySelector('[data-ck-notify-error]');
        const email = notify.querySelector('[data-ck-notify-email]');
        const submitBtn = notify.querySelector('[data-ck-notify-submit]');
        if (form) form.hidden = false;
        if (success) success.hidden = true;
        if (error) error.hidden = true;
        if (email) email.value = '';
        if (submitBtn) submitBtn.disabled = false;
      }

      async handleNotifySubmit(btn) {
        const notify = btn.closest('[data-ck-qs-notify]');
        if (!notify) return;

        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) return;

        const emailInput = notify.querySelector('[data-ck-notify-email]');
        const errorEl = notify.querySelector('[data-ck-notify-error]');
        const successEl = notify.querySelector('[data-ck-notify-success]');
        const formEl = notify.querySelector('[data-ck-notify-form]');

        if (errorEl) errorEl.hidden = true;

        if (!emailInput || !emailInput.checkValidity()) {
          if (emailInput) emailInput.reportValidity();
          return;
        }

        if (!window.theme || !theme.CkBackInStock) return;

        btn.disabled = true;

        try {
          await theme.CkBackInStock.subscribe(emailInput.value.trim(), variantId);
          if (formEl) formEl.hidden = true;
          if (successEl) successEl.hidden = false;
        } catch (err) {
          btn.disabled = false;
          if (errorEl) errorEl.hidden = false;
        }
      }

      handleClick(e) {
        const notifyClose = e.target.closest('[data-ck-notify-inline-close]');
        if (notifyClose) {
          const notify = notifyClose.closest('[data-ck-qs-notify]');
          if (notify) notify.hidden = true;
          return;
        }

        const notifySubmit = e.target.closest('[data-ck-notify-submit]');
        if (notifySubmit) {
          this.handleNotifySubmit(notifySubmit);
          return;
        }

        // Clicking a size that's already selected doesn't fire a native `change` event (the
        // radio's checked state doesn't actually change), so handleChange() never re-runs.
        // Without this, dismissing the notify overlay via the back arrow then clicking the
        // same still-sold-out size again would do nothing — it'd only reopen on a genuine
        // switch to a different size.
        const sizeLabel = e.target.closest('.ck-qs-compact__option--size .ck-qs-compact__option-btn');
        if (sizeLabel && !this.classList.contains('ck-qs-compact--locked')) {
          const input = document.getElementById(sizeLabel.getAttribute('for'));
          if (input && input.checked && input.classList.contains('variant__input--unavailable')) {
            const notify = this.querySelector('[data-ck-qs-notify]');
            if (notify) {
              notify.hidden = false;
              this.resetNotify(notify);
              this.syncNotifyPanel(notify);
            }
            return;
          }
        }

        const swatch = e.target.closest('.ck-qs-compact__swatches .color-swatch');
        if (!swatch) return;

        // No-op on the already-active swatch.
        if (swatch.classList.contains('is-active')) {
          e.preventDefault();
          return;
        }

        const href = swatch.getAttribute('href');
        if (!href) return;

        const match = href.match(/\/products\/([^/?#]+)/);
        if (!match) return;

        e.preventDefault();
        e.stopPropagation();
        this.swapProduct(match[1]);
      }

      async swapProduct(handle) {
        // Wishlist mode: pull the sibling's pre-rendered section from its own
        // WishlistModal already in the DOM — no AJAX fetch needed.
        if (this.dataset.mode === 'wishlist') {
          await this._swapWishlistProduct(handle);
          return;
        }

        const modal = this.closest('.modal--quick-shop');
        if (modal) modal.classList.add('is-ck-qs-loading');

        try {
          const root =
            (window.theme && window.theme.routes && window.theme.routes.home) || '';
          const url = (root + '/products/' + handle + '?view=modal').replace(
            '//',
            '/'
          );

          const res = await fetch(url);
          if (!res.ok) throw new Error('Fetch failed: ' + res.status);
          const text = await res.text();

          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newSection = doc.querySelector(
            '.product-section[data-product-handle="' + handle + '"]'
          );
          if (!newSection) throw new Error('Section not found in response');

          const oldSection = this.closest('.product-section');
          if (!oldSection || !oldSection.parentNode) return;
          const parent = oldSection.parentNode;

          parent.replaceChild(newSection, oldSection);

          // Re-register theme.Product on the new section so form/variants/ATC wire up.
          if (window.theme && window.theme.sections && window.theme.Product) {
            window.theme.sections.register('product', window.theme.Product, parent);
          }

          // Modal is already visually open — fire modalOpen for the new product
          // so the freshly-registered Product instance runs its form setup.
          const newProductId = newSection.dataset.productId;
          if (newProductId) {
            document.dispatchEvent(
              new CustomEvent('modalOpen.QuickShopModal-' + newProductId)
            );
          }
        } catch (err) {
          console.error('CK | Quick shop compact swap failed:', err);
        } finally {
          if (modal) modal.classList.remove('is-ck-qs-loading');
        }
      }

      async _swapWishlistProduct(handle) {
        const currentModal = this.closest('.modal--wishlist');
        const modalId = currentModal && currentModal.id;

        if (currentModal) currentModal.classList.add('is-ck-qs-loading');

        try {
          const root =
            (window.theme && window.theme.routes && window.theme.routes.home) || '';
          const url = (root + '/products/' + handle + '?view=ck-wishlist-modal').replace(
            '//',
            '/'
          );

          const res = await fetch(url);
          if (!res.ok) throw new Error('Fetch failed: ' + res.status);
          const text = await res.text();

          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newSection = doc.querySelector(
            '.product-section[data-product-handle="' + handle + '"]'
          );
          if (!newSection) throw new Error('Section not found in response');

          const oldSection = this.closest('.product-section');
          if (!oldSection || !oldSection.parentNode) return;
          const parent = oldSection.parentNode;

          // First swap: cache the original section so it can be restored when
          // the modal closes — keeps it correct for the next open.
          if (!currentModal._ckOriginalSection) {
            currentModal._ckOriginalSection = oldSection.cloneNode(true);
            if (modalId) {
              document.addEventListener('modalClose.' + modalId, function restoreOnClose() {
                document.removeEventListener('modalClose.' + modalId, restoreOnClose);
                var cur = parent.querySelector('.product-section');
                if (cur && currentModal._ckOriginalSection) {
                  parent.replaceChild(currentModal._ckOriginalSection.cloneNode(true), cur);
                }
                currentModal._ckOriginalSection = null;
              });
            }
          }

          parent.replaceChild(newSection, oldSection);

        } catch (err) {
          console.error('CK | Wishlist compact swap failed:', err);
        } finally {
          if (currentModal) currentModal.classList.remove('is-ck-qs-loading');
        }
      }
    }
  );
}

// Quick shop modals are rendered inside product grid items, which on mobile can sit
// inside overflow:scroll containers (grid-overflow-wrapper). iOS Safari clips or
// scrolls position:fixed elements inside overflow:scroll ancestors. Moving every
// modal to <body> fixes this — IDs and content are unchanged.
// Exposed as window.ckHoistModals so dynamically-injected sections (e.g. recently
// viewed) can call it again after their cards are appended.
window.ckHoistModals = function () {
  document.querySelectorAll('.modal--quick-shop').forEach(function (modal) {
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
  });
};
(function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.ckHoistModals);
  } else {
    window.ckHoistModals();
  }
  // Re-hoist after product recommendations load their cards via AJAX.
  document.addEventListener('recommendations:loaded', window.ckHoistModals);
})();

// Yotpo widgets injected via AJAX are not auto-initialized by Yotpo's script.
// Refresh them whenever a quick-shop modal opens (covers both desktop and mobile).
(function () {
  document.addEventListener('modalOpen', function () {
    var activeModal = document.querySelector('.modal--quick-shop.modal--is-active');
    if (!activeModal) return;
    if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
      window.yotpo.refreshWidgets();
    }
  });
})();

// On desktop, theme.initQuickShop preloads modal content on mouseover/focusin.
// On mobile that event never fires, so btn--not-ready is never removed and tapping does nothing.
// This intercepts the first tap on a not-ready quick-shop button, runs the same preload,
// then re-clicks the button once the modal content and listeners are ready.
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.quick-product__btn.btn--not-ready');
    if (!btn) return;
    if (!window.theme || !theme.config || !theme.config.bpSmall) return;

    var product = btn.closest('.grid-product');
    if (!product) return;

    var handle = product.dataset.productHandle;
    var productId = product.dataset.productId;
    if (!handle || !productId) return;

    var holder = document.getElementById('QuickShopHolder-' + handle);
    if (!holder) return;

    var root = (theme.routes && theme.routes.home) || '';
    var url = (root + '/products/' + handle + '?view=modal').replace('//', '/');

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var html = document.createElement('div');
        html.innerHTML = text;
        var section = html.querySelector('.product-section[data-product-handle="' + handle + '"]');
        if (!section) return;

        holder.innerHTML = '';
        holder.append(section);

        new theme.Modals('QuickShopModal-' + productId, 'quick-modal-' + productId);
        theme.sections.register('product', theme.Product, holder);
        theme.collapsibles.init();

        // btn--not-ready is now removed and the click-to-open listener is attached — fire it
        btn.click();
      })
      .catch(function (err) {
        console.error('CK | Quick shop mobile preload failed:', err);
      });
  }, true);
})();
